<script setup lang="ts">
import UiModalBottom from '@boats/ui/components/UiModalBottom.vue'
import IconPicker from 'ui/src/components/icons/IconPicker.vue'

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppButtonSelect from './AppButtonSelect.vue'

type Option = {
  label: string
  value: string
}

type Props = {
  title: string
  options: Option[]
  pinned?: []
  modelValue: any
  fullWidth?: boolean
  class?: string
  icon?: string
  hint?: string
  scrollable?: boolean
  error?: boolean
}

const emit = defineEmits(['update:modelValue'])

const props = defineProps<Props>()

const selected = ref<Option>(props.options[0])
const scrollable = ref(props.scrollable ?? false)
const pinned = ref(props.pinned ?? []);
const modalOpen = ref(false)
const modalSelected = ref<Option>(selected.value)
const selectIcon = ref<string>(props.icon || 'clock')
const selectHint = ref<string|undefined>(props?.hint)
const listBoxRef = ref(null)
let observer = null
const isOpen = ref(false)

const classes = computed(() => ({
  'app-select': true,
  [`${props.class}`]: !!props.class,
  'app-select--full-width': props.fullWidth,
  'z-50': modalOpen.value || isOpen.value,
}))

watch(() => props.hint, (newHint) => {
  selectHint.value = newHint;
});

watch(selected, (value) => (modalSelected.value = value))

const openModal = () => {
  if (modalSelected.value !== selected.value) {
    modalSelected.value = selected.value
  }

  modalOpen.value = true
}

const isOptionSelected = (option: Option) => {
  return option.value == props.modelValue?.value
}

const closeModal = () => (modalOpen.value = false)

const submitModal = () => {
  selected.value = modalSelected.value

  closeModal()
}

const closeModalOnDesktop = () => {
  const mql = window.matchMedia('(min-width: 768px)')

  if (mql.matches) {
    closeModal()
  }
}

onMounted(() => {
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName === 'aria-expanded') {
        const newValue = listBoxRef.value?.el.getAttribute('aria-expanded')
        isOpen.value = newValue === 'true'
      }
    }
  })

  observer.observe(listBoxRef.value?.el, { attributes: true })
  window.addEventListener('resize', closeModalOnDesktop)
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
  window.removeEventListener('resize', closeModalOnDesktop)
})

const updateValue = (value: any) => {
  emit('update:modelValue', value)
}
</script>

<template>
  <div :class="classes">
    <Listbox :model-value="modelValue" @update:model-value="updateValue">
      <ListboxButton ref="listBoxRef" class="app-select__button !hidden w-full md:!flex">
        <AppButtonSelect as="div" :aria-expanded="ariaExpanded" :full-width="fullWidth" :class="[{'app-select-field--error': props.error}]">
          {{ modelValue?.label || title }}
          <template #icon><IconPicker :name="selectIcon"/></template>
        </AppButtonSelect>
        <span v-if="selectHint" class="app-select-hint" @click.stop v-html="selectHint"></span>
      </ListboxButton>

      <transition name="fade">
        <ListboxOptions :class="['app-select__options !hidden md:!block', { 'scrollable-options': scrollable }]">
          <ListboxOption
            v-for="option in options"
            v-slot="{ active }"
            :key="option.value"
            :value="option"
            as="template"
          >
            <li
              :class="{
                active: active,
                selected: isOptionSelected(option),
                'app-select__option text-secondary-nav': true,
              }"
            >
              <span class="app-select__option-label">{{ option.label }}</span>
              <span v-if="!isOptionSelected(option) && pinned.map(String).includes(`${option.value}`)" class="app-select__option-icon app-select__option-icon-inverse">
                <IconMapPin/>
              </span>
              <span v-if="isOptionSelected(option)" class="app-select__option-icon">
                <IconCheck />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </Listbox>
    <AppButtonSelect
      :full-width="fullWidth"
      class="md:!hidden"
      @click="openModal"
    >
      {{ modelValue?.label || title }}
      <template #icon><IconPicker :name="selectIcon"/></template>
    </AppButtonSelect>

    <UiModalBottom v-model="modalOpen" class="app-select__modal">
      <template #header>
        <span class="app-select__modal-title">{{ title }}</span>
      </template>

      <Listbox :model-value="modelValue" @update:model-value="updateValue">
        <ListboxOptions class="app-select__modal-options" :static="true">
          <ListboxOption
            v-for="option in options"
            v-slot="{ active }"
            :key="option.value"
            :value="option"
            as="template"
          >
            <li
              :class="{
                active: active,
                selected: isOptionSelected(option),
                'app-select__modal-option': true,
              }"
            >
              <span class="app-select__modal-option-label">
                {{ option.label }}
              </span>
              <span v-if="!isOptionSelected(option) && pinned.map(String).includes(`${option.value}`)" class="app-select__option-icon app-select__option-icon-inverse">
                <IconMapPin/>
              </span>
              <span v-if="isOptionSelected(option)" class="app-select__option-icon">
                <IconCheck />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </Listbox>

      <div class="row mt-auto">
        <div class="col-6">
          <AppButton variant="outlined" full-width @click="closeModal">
            {{ $t('string.Close') }}
          </AppButton>
        </div>

        <div class="col-6">
          <AppButton full-width @click="submitModal">
            {{ $t('string.Select') }}
          </AppButton>
        </div>
      </div>
    </UiModalBottom>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-select {
  position: relative;
  display: inline-flex;
  // z-index: 900;
}

.app-select--full-width {
  display: flex;
  width: 100%;

  // Match the menu to the full-width button instead of the fixed 300px.
  .app-select__options {
    width: 100%;
  }
}

// .app-select__button {
//   display: flex;
//   align-items: center;
//   width: 100%;
//   padding: 0.5rem;
//   font-family: $font-barlow;
//   font-size: 0.875rem;
//   line-height: 1.14285714;
//   color: $black;
//   background-color: $gray-100;
//   border: 1px solid $gray-200;
//   border-radius: 0.125rem;
//   user-select: none;
//   cursor: pointer;
//   @include transition('color, background-color, border-color');

//   &:hover {
//     background-color: $gray-200;
//   }

//   &:focus {
//     outline: none;
//     color: $white;
//     background-color: $black;
//     border-color: $black;
//   }

//   span {
//     margin-right: 0.25rem;
//   }

//   .svg-icon {
//     position: relative;
//     width: 1rem;
//     top: 1px;
//     margin-left: auto;
//   }

//   @include media-breakpoint-up(md) {
//     padding: 0.6875rem 0.75rem;
//     font-size: 1rem;
//     line-height: 1.25;

//     span {
//       margin-right: 0.5rem;
//     }

//     .svg-icon {
//       width: 1.25rem;
//     }
//   }
// }

.app-select__options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 18.75rem;
  margin: 0;
  padding: 0.75rem;
  color: $black;
  list-style: none;
  background-color: $white;
  border: 1px solid $gray-100;
  border-radius: 0.5rem;
  box-shadow: 0.125rem 0.625rem 2rem 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.app-select-hint {
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 0.875rem;
  color: #076CD9;
  margin-top: 4px;
  text-decoration: underline;
  cursor: pointer;
}

.app-select__option {
  position: relative;
  display: flex;
  text-align: left;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.25rem;
  padding: 0.625rem 3.375rem 0.625rem 0.625rem;
  border-radius: 0.125rem;
  cursor: pointer;
  @include transition('background-color');

  &:last-child {
    margin-bottom: 0;
  }

  &-icon {
    position: absolute;
    top: 50%;
    right: 0.625rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    color: $white;
    background-color: $black;
    border-radius: 50%;
    transform: translateY(-50%);

    &-inverse {
      background-color: $white;
      color: $black;
    }

    svg {
      width: 1rem;
    }
  }

  &.active {
    background-color: $gray-100;
  }
}

.scrollable-options {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
}

.scrollable-options::-webkit-scrollbar {
  width: 8px;
}

.scrollable-options::-webkit-scrollbar-thumb {
  background-color: #999;
  border-radius: 10px;
}

.app-select__modal {
  .ui-modal-bottom__content-wrapper {
    display: flex;
    flex-direction: column;
  }

  .ui-modal-bottom__content {
    overflow-y: auto;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
  }
}

.app-select__modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: $black;
}

.app-select__modal-options {
  margin: 0 0 1.5rem;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  border-top: 1px solid $gray-200;
}

.app-select-field--error {
  background-color: $white;
  border-color: $signal-error-red;
}

.app-select__modal-option {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 3.25rem;
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.00588235em;
  color: $black;
  border-bottom: 1px solid $gray-200;
  cursor: pointer;
  @include transition('color');

  &:hover {
    color: $red;
    text-decoration: none;
  }
}
</style>
